import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner, toast as sonnerToast } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { LocationProvider } from "@/contexts/LocationContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useEffect, lazy, Suspense } from "react";
import { PageTransition } from "@/components/PageTransition";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { supabase } from "@/integrations/supabase/client";
import { useAutoClaimCheck } from "@/hooks/useAutoClaimCheck";
import MobileNav from "./components/MobileNav";
import GlobalHeader from "./components/GlobalHeader";
import PublicLayout from "./components/PublicLayout";
import LangLayout from "./components/LangLayout";
import { PreviewSheetPortal } from "@/components/cards/PreviewSheetPortal";
import { Loader2 } from "lucide-react";

// Lazy-loaded pages for reduced initial bundle
const MatchCalculatorPage = lazy(() => import("./pages/MatchCalculatorPage"));
const Landing = lazy(() => import("./pages/Landing"));
const Calculator = lazy(() => import("./pages/Calculator"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const Studio = lazy(() => import("./pages/Studio"));
const AdminSuperControl = lazy(() => import("./pages/AdminSuperControl"));
const DemoCalculator = lazy(() => import("./pages/DemoCalculator"));
const LeadView = lazy(() => import("./pages/LeadView"));
const CityLanding = lazy(() => import("./pages/CityLanding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Success = lazy(() => import("./pages/Success"));
const Privacy = lazy(() => import("./pages/Privacy"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Support = lazy(() => import("./pages/Support"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const StyleCityLanding = lazy(() => import("./pages/StyleCityLanding"));
const MyVault = lazy(() => import("./pages/MyVault"));
const Trends = lazy(() => import("./pages/Trends"));
const TattooIndex = lazy(() => import("./pages/TattooIndex"));
const TattooCountry = lazy(() => import("./pages/TattooCountry"));
const TattooCity = lazy(() => import("./pages/TattooCity"));
const TattooStudio = lazy(() => import("./pages/TattooStudio"));
const RedirectStudio = lazy(() => import("./pages/RedirectStudio"));
const TattooStyle = lazy(() => import("./pages/TattooStyle"));
const ClaimCity = lazy(() => import("./pages/ClaimCity"));
const ArtistDirectory = lazy(() => import("./pages/ArtistDirectory"));
const StyleWiki = lazy(() => import("./pages/StyleWiki"));
const StyleWikiIndex = lazy(() => import("./pages/StyleWikiIndex"));
const GGDCertificering = lazy(() => import("./pages/GGDCertificering"));
const InstagramCallback = lazy(() => import("./pages/InstagramCallback"));
const StudioDirectory = lazy(() => import("./pages/StudioDirectory"));
const InkSwipePage = lazy(() => import("./pages/InkSwipePage"));
const ClientAccountPage = lazy(() => import("./pages/ClientAccountPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-primary animate-spin" />
    </div>
  );
}

function AutoClaimWrapper() {
  useAutoClaimCheck();
  return null;
}

function AppContent() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isEmbed = searchParams.get("embed") === "true";
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    setIsStandalone(window.matchMedia("(display-mode: standalone)").matches);
  }, []);

  const hideNav =
    isEmbed ||
    ["/auth", "/admin", "/admin-super-control", "/dashboard", "/swipe", "/account"].includes(location.pathname) ||
    location.pathname.startsWith("/c/");
  const showMobileNav = isMobile && !hideNav;

  return (
    <PageTransition>
      <AutoClaimWrapper />
      <GlobalHeader />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/c/:slug" element={<Calculator />} />
          <Route path="/c/code/:code" element={<Calculator />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/auth/callback" element={<Auth />} />
          <Route path="/auth/instagram/callback" element={<InstagramCallback />} />
          <Route path="/admin" element={<AdminSuperControl />} />
          <Route path="/upgrade" element={<Upgrade />} />
          <Route path="/success" element={<Success />} />
          <Route path="/studio" element={<Studio />} />
          <Route path="/studio/:studioSlug" element={<RedirectStudio />} />
          <Route path="/admin-super-control" element={<AdminSuperControl />} />
          <Route path="/demo-calculator" element={<DemoCalculator />} />
          <Route path="/playground" element={<DemoCalculator />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/support" element={<Support />} />
          <Route path="/vind-een-artiest" element={<MatchCalculatorPage />} />
          <Route path="/hoe-werkt-het" element={<HowItWorks />} />
          <Route path="/mijn-kluis" element={<MyVault />} />
          <Route path="/trends" element={<Trends />} />
          <Route path="/swipe" element={<InkSwipePage />} />
          <Route path="/account" element={<ClientAccountPage />} />
          <Route path="/tatoeerders/:city" element={<CityLanding />} />
          <Route path="/tattoo-stijl/:style/:city" element={<StyleCityLanding />} />
          <Route path="/:style/:city" element={<StyleCityLanding />} />

          {/* Public routes with shared header/footer */}
          <Route element={<PublicLayout />}>
            <Route path="/tattoo" element={<TattooIndex />} />
            <Route path="/tattoo/:countrySlug" element={<TattooCountry />} />
            <Route path="/tattoo/:countrySlug/:citySlug" element={<TattooCity />} />
            <Route path="/tattoo/:countrySlug/:citySlug/:studioSlug" element={<TattooStudio />} />
            <Route path="/tattoo/:countrySlug/:citySlug/stijl/:styleSlug" element={<TattooStyle />} />
            <Route path="/lead/:id" element={<LeadView />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/ontdekken" element={<Navigate to="/artiesten" replace />} />
            <Route path="/artiesten" element={<ArtistDirectory />} />
            <Route path="/directory" element={<StudioDirectory />} />
            <Route path="/wiki" element={<StyleWikiIndex />} />
            <Route path="/wiki/ggd-certificering" element={<GGDCertificering />} />
            <Route path="/wiki/:styleSlug" element={<StyleWiki />} />
            <Route path="/claim" element={<ClaimCity />} />
            <Route path="/claim/:citySlug" element={<ClaimCity />} />
          </Route>

          {/* ═══ Lang-prefixed routes: /:lang/... for international SEO ═══ */}
          <Route path="/:lang" element={<LangLayout />}>
            <Route index element={<Landing />} />
            <Route path="c/:slug" element={<Calculator />} />
            <Route path="demo-calculator" element={<DemoCalculator />} />
            <Route path="find-an-artist" element={<MatchCalculatorPage />} />
            <Route path="hoe-werkt-het" element={<HowItWorks />} />
            <Route path="mijn-kluis" element={<MyVault />} />
            <Route path="trends" element={<Trends />} />
            <Route path="swipe" element={<InkSwipePage />} />
            <Route path="account" element={<ClientAccountPage />} />
            <Route element={<PublicLayout />}>
              <Route path="tattoo" element={<TattooIndex />} />
              <Route path="tattoo/:countrySlug" element={<TattooCountry />} />
              <Route path="tattoo/:countrySlug/:citySlug" element={<TattooCity />} />
              <Route path="tattoo/:countrySlug/:citySlug/:studioSlug" element={<TattooStudio />} />
              <Route path="tattoo/:countrySlug/:citySlug/stijl/:styleSlug" element={<TattooStyle />} />
              <Route path="blog" element={<Blog />} />
              <Route path="blog/:slug" element={<BlogPost />} />
              <Route path="ontdekken" element={<Navigate to="../artiesten" replace />} />
              <Route path="artiesten" element={<ArtistDirectory />} />
              <Route path="directory" element={<StudioDirectory />} />
              <Route path="wiki" element={<StyleWikiIndex />} />
              <Route path="wiki/ggd-certification" element={<GGDCertificering />} />
              <Route path="wiki/:styleSlug" element={<StyleWiki />} />
              <Route path="claim" element={<ClaimCity />} />
              <Route path="claim/:citySlug" element={<ClaimCity />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {showMobileNav && <MobileNav />}
      {showMobileNav && <div className="h-16" />}
    </PageTransition>
  );
}

/** Load platform theme from app_settings — only sets gold token overrides, does NOT touch core theme variables */
function useGlobalTheme() {
  useEffect(() => {
    supabase
      .from("app_settings")
      .select("value")
      .eq("key", "platform_theme")
      .maybeSingle()
      .then(({ data }) => {
        if (!data?.value) return;
        const theme = data.value as any;
        // Platform theme only overrides gold accent tokens — core theme (background, foreground, card etc.)
        // is managed by ThemeContext and CSS data-theme attribute to avoid conflicts.
        const h = theme.primary_hue ?? 43;
        const s = theme.primary_saturation ?? 68;
        const l = theme.primary_lightness ?? 46;
        document.documentElement.style.setProperty("--gold", `${h} ${s}% ${l}%`);
        document.documentElement.style.setProperty(
          "--gold-dim",
          `${h} ${Math.max(s - 28, 10)}% ${Math.max(l - 14, 10)}%`,
        );
        document.documentElement.style.setProperty(
          "--gold-glow",
          `${h} ${Math.min(s + 12, 100)}% ${Math.min(l + 12, 85)}%`,
        );
      });
  }, []);
}

/** Listen for VitePWA Service Worker updates and show a toast */
function useSWUpdateToast() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    let reloading = false;
    const onControllerChange = () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    };

    const showUpdate = (sw: ServiceWorker) => {
      sonnerToast("Nieuwe versie beschikbaar", {
        description: "Klik hier om te vernieuwen.",
        action: {
          label: "Vernieuwen",
          onClick: () => sw.postMessage({ type: "SKIP_WAITING" }),
        },
        duration: Infinity,
      });
    };

    let pollId: ReturnType<typeof setInterval> | undefined;
    let focusHandler: (() => void) | undefined;

    const init = async () => {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return;

      if (reg.waiting && navigator.serviceWorker.controller) {
        showUpdate(reg.waiting);
      }

      reg.addEventListener("updatefound", () => {
        const newSW = reg.installing;
        if (!newSW) return;
        newSW.addEventListener("statechange", () => {
          if (newSW.state === "installed" && navigator.serviceWorker.controller) {
            showUpdate(newSW);
          }
        });
      });

      // Actief checken op updates elke 60s + bij window focus
      const check = () => reg.update().catch(() => {});
      pollId = setInterval(check, 60_000);
      focusHandler = check;
      window.addEventListener("focus", focusHandler);
    };

    init();
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      if (pollId) clearInterval(pollId);
      if (focusHandler) window.removeEventListener("focus", focusHandler);
    };
  }, []);
}

const App = () => {
  useGlobalTheme();
  useSWUpdateToast();

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LocationProvider>
          <I18nProvider>
            <ThemeProvider>
              <AuthProvider>
                <ImpersonationProvider>
                  <TooltipProvider>
                    <Toaster />
                    <Sonner />
                    <BrowserRouter>
                      <AppContent />
                      <PreviewSheetPortal />
                    </BrowserRouter>
                  </TooltipProvider>
                </ImpersonationProvider>
              </AuthProvider>
            </ThemeProvider>
          </I18nProvider>
        </LocationProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
